import { useCallback, useContext, useEffect, useState } from "react";

import SaeDao from "../../dao/SaeDao";

import { DASH_LINKS } from "../../const/AppCardLinks";
import { formatDate } from "../DateFormatter";
import { GlobalContext } from "../../components/contextProvider/ContextProvider";
import { isWindowOpen } from "../ActionControlWindows";

/**
 * ASSESSMENT EXCUSALS BUSINESS LOGIC
 * ------------------------------------------------------------------------------------------------
 * Student is NOT GradeLevelType: THIRTEENTH
 * Student must have a StudentNumber
 * Action Window assessmentLibrary->PARENT_REQUEST for the Active user SchoolYear is open
 * get optOutAssessments = /student-info/optOut/byStudentNumber/{$studentNumber}/{$schoolYearId}/index.json
 * Update State Assessment Excusal Form -> https://student-excusals.dcsdk12.org/{studentNumber}
 * Status: 0 Excusals or 1 Excusal, etc.,
 * No "More Info" button
 * main button is contextual - request or manage assessment excusals
 * ------------------------------------------------------------------------------------------------
 * Custom hook to determine if a student's guardian has access to Assessment Excusal
 * @name useAeApp
 * @param {{}} student
 * @returns {obj} {appObj, loading}
 */
export const useAeApp = (student) => {
    const { dispatch, state } = useContext(GlobalContext);
    const { saeActionControls, schoolYearDto, studentAppObjects, token } = state || {};

    const [appObj, setAppObj] = useState(null);
    const [loading, setLoading] = useState(true);

    const studentKey = student?.key;
    const existing = studentAppObjects?.[studentKey]?.ASSESSMENT_EXCUSALS || null;

    /**
     * @name dispatchAppObject
     * @description Dispatch the appObj to the context
     * @param {{}} obj
     * @callback
     */
    const dispatchAppObject = useCallback(
        (obj) => {
            dispatch({
                type: "StudentAppObjects",
                studentAppObjects: {
                    ...studentAppObjects,
                    [studentKey]: {
                        ...studentAppObjects?.[studentKey],
                        ASSESSMENT_EXCUSALS: {
                            appObj: obj
                        }
                    }
                }
            });
        },
        [dispatch, studentAppObjects, studentKey]
    );

    /**
     * If we have the student's apps in context, set the appObj for return
     */
    useEffect(() => {
        if (existing) {
            setAppObj(existing.appObj);
            setLoading(false);
        }
    }, [existing]);

    /**
     * If the SAE PARENT_REQUEST window is open, construct the appObj and a count of the student's excusals, if any
     */
    useEffect(() => {
        if (existing) {
            return;
        }
        if (
            !schoolYearDto ||
            !token ||
            !student?.studentNumber ||
            ["THIRTEENTH", 13, "13"].includes(student?.gradeName)
        ) {
            setAppObj(null);
            setLoading(false);
            return;
        }

        setLoading(true); // Start loading

        /**
         * @name fetchSaeRequestWindow
         * @description Check if the SAE PARENT_REQUEST window is open
         * @async
         * @returns {{} | null}
         */
        const fetchSaeRequestWindow = () => {
            try {
                const controlMatch = saeActionControls?.filter((obj) => obj.action === "PARENT_REQUEST");
                if (controlMatch && controlMatch.length > 0) {
                    if (isWindowOpen(controlMatch[0].startDate, controlMatch[0].endDate)) {
                        return controlMatch[0];
                    }
                }

                return null;
            } catch (error) {
                console.error(`Unable to find SAE Action Controls for School Year ${schoolYearDto.key}:\n`, error);
            }
        };

        /**
         * @name fetchAssessmentExcusalApp
         * @description Construct the Assessment Excusal App
         * @async
         * @param {{}} requestWindow
         * @returns {{} | null}
         */
        const fetchAssessmentExcusalApp = async (requestWindow) => {
            try {
                const options = {
                    action: "saeOptOutsByStudentRead",
                    schoolYearKey: schoolYearDto.key,
                    studentNumber: student.studentNumber,
                    token
                };
                const response = await SaeDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    const excusalObj = {
                        app: "ASSESSMENT_EXCUSALS",
                        // Manage assessment opt-out requests.
                        body: "AEApp_Body",
                        buttons: [
                            {
                                label: "AEApp_Btn1",
                                href: `${DASH_LINKS.assessment_excusals}/${student.studentNumber}`
                            }
                        ],
                        endDate: formatDate(requestWindow?.endDate, true, true),
                        icon: "ASSESSMENT_EXCUSAL_ICON",
                        moreInfo: null,
                        startDate: formatDate(requestWindow?.startDate, true, true),
                        // Excusals : No Excusals
                        status: payload.length > 0 ? "AEApp_MoreExcusals" : "AEApp_NoExcusals",
                        statusColor: payload.length > 0 ? "#0669B3" : "#707070", // pearl-blue : grey3a
                        statusNumber: payload.length > 0 ? payload.length : null,
                        subStatus: payload.length > 0 ? "Global_Completed" : null,
                        subStatusRange: payload.length > 0 ? formatDate(payload[0].optOutDate, true, true) : null,
                        title: "AEApp_Header"
                    };

                    return excusalObj;
                }

                return null;
            } catch (error) {
                console.error("Unable to retrieve Assessment Excusals App:\n", error);
            }
        };

        const run = async () => {
            try {
                const saeRequestWindow = fetchSaeRequestWindow();
                const app = saeRequestWindow ? await fetchAssessmentExcusalApp(saeRequestWindow) : null;
                dispatchAppObject(app);
            } catch (error) {
                console.error(
                    `Unable to retrieve Assessment Excusals details for student ${student.studentNumber}:\n`,
                    error
                );
            } finally {
                setLoading(false);
            }
        };

        run();
    }, [dispatchAppObject, existing, saeActionControls, student, schoolYearDto, token]);

    // return the appObj and its loading state
    return { appObj, loading };
};
